// 세금계산서 관련 번역 및 상수

import type { Language, Locale } from '@/config/locales';
import type { InvoiceTranslations } from './types';

// #522 — Locale 단일 출처 정합 (15언어 전수).
export const invoiceTranslations:
  & Record<Language, InvoiceTranslations>
  & Record<Exclude<Locale, Language>, InvoiceTranslations> = {
  ko: {
    invoiceGenerated: '세금계산서 발급 완료',
    success: '성공적으로 발급되었습니다!',
    invoiceNumber: '세금계산서 번호',
    uuid: 'UUID',
    company: '회사',
    generatedAt: '발급 시간',
    downloadOptions: '다운로드 및 공유',
    downloadPDF: 'PDF 다운로드',
    downloadXML: 'XML 다운로드',
    emailSend: '이메일 발송',
    whatsappSend: 'WhatsApp',
    smsSend: '문자 발송',
    copyLink: '링크 복사',
    viewOnline: '온라인으로 보기',
    done: '완료',
    linkCopied: '링크가 복사되었습니다',
    emailSent: '이메일이 전송되었습니다',
    important: '중요',
    keepRecord: '이 세금계산서를 안전하게 보관해주세요.'
  },
  es: {
    invoiceGenerated: 'Factura Generada',
    success: '!Su CFDI ha sido generado exitosamente!',
    invoiceNumber: 'Folio de factura',
    uuid: 'UUID',
    company: 'Empresa',
    generatedAt: 'Generado el',
    downloadOptions: 'Descargar y Compartir',
    downloadPDF: 'Descargar PDF',
    downloadXML: 'Descargar XML',
    emailSend: 'Enviar por email',
    whatsappSend: 'WhatsApp',
    smsSend: 'Enviar por SMS',
    copyLink: 'Copiar enlace',
    viewOnline: 'Ver en linea',
    done: 'Finalizar',
    linkCopied: 'Enlace copiado',
    emailSent: 'Email enviado',
    important: 'Importante',
    keepRecord: 'Conserve este comprobante fiscal para sus registros contables.'
  },
  en: {
    invoiceGenerated: 'Invoice Generated',
    success: 'Your CFDI has been successfully generated!',
    invoiceNumber: 'Invoice folio',
    uuid: 'UUID',
    company: 'Company',
    generatedAt: 'Generated at',
    downloadOptions: 'Download & Share',
    downloadPDF: 'Download PDF',
    downloadXML: 'Download XML',
    emailSend: 'Send via email',
    whatsappSend: 'WhatsApp',
    smsSend: 'Send via SMS',
    copyLink: 'Copy link',
    viewOnline: 'View online',
    done: 'Done',
    linkCopied: 'Link copied',
    emailSent: 'Email sent',
    important: 'Important',
    keepRecord: 'Please keep this tax receipt for your accounting records.'
  },
  pt: {
    invoiceGenerated: 'Fatura gerada',
    success: 'Seu CFDI foi gerado com sucesso!',
    invoiceNumber: 'Folio da fatura',
    uuid: 'UUID',
    company: 'Empresa',
    generatedAt: 'Gerado em',
    downloadOptions: 'Baixar e compartilhar',
    downloadPDF: 'Baixar PDF',
    downloadXML: 'Baixar XML',
    emailSend: 'Enviar por e-mail',
    whatsappSend: 'WhatsApp',
    smsSend: 'Enviar por SMS',
    copyLink: 'Copiar link',
    viewOnline: 'Ver on-line',
    done: 'Concluir',
    linkCopied: 'Link copiado',
    emailSent: 'E-mail enviado',
    important: 'Importante',
    keepRecord: 'Guarde este comprovante fiscal para seus registros contábeis.'
  },
  fr: {
    invoiceGenerated: 'Facture générée',
    success: 'Votre CFDI a été généré avec succès !',
    invoiceNumber: 'Folio de la facture',
    uuid: 'UUID',
    company: 'Entreprise',
    generatedAt: 'Généré le',
    downloadOptions: 'Télécharger et partager',
    downloadPDF: 'Télécharger le PDF',
    downloadXML: 'Télécharger le XML',
    emailSend: 'Envoyer par e-mail',
    whatsappSend: 'WhatsApp',
    smsSend: 'Envoyer par SMS',
    copyLink: 'Copier le lien',
    viewOnline: 'Voir en ligne',
    done: 'Terminer',
    linkCopied: 'Lien copié',
    emailSent: 'E-mail envoyé',
    important: 'Important',
    keepRecord: 'Conservez ce justificatif fiscal pour votre comptabilité.'
  },
  zh: {
    invoiceGenerated: '发票已生成',
    success: '您的 CFDI 已成功生成！',
    invoiceNumber: '发票编号',
    uuid: 'UUID',
    company: '公司',
    generatedAt: '生成时间',
    downloadOptions: '下载与分享',
    downloadPDF: '下载 PDF',
    downloadXML: '下载 XML',
    emailSend: '通过邮箱发送',
    whatsappSend: 'WhatsApp',
    smsSend: '通过短信发送',
    copyLink: '复制链接',
    viewOnline: '在线查看',
    done: '完成',
    linkCopied: '链接已复制',
    emailSent: '邮件已发送',
    important: '重要',
    keepRecord: '请妥善保管此税务凭证以备会计记录。'
  },
  ja: {
    invoiceGenerated: '請求書を発行しました',
    success: 'CFDI が正常に発行されました！',
    invoiceNumber: '請求書番号',
    uuid: 'UUID',
    company: '会社',
    generatedAt: '発行日時',
    downloadOptions: 'ダウンロードと共有',
    downloadPDF: 'PDF をダウンロード',
    downloadXML: 'XML をダウンロード',
    emailSend: 'メールで送信',
    whatsappSend: 'WhatsApp',
    smsSend: 'SMS で送信',
    copyLink: 'リンクをコピー',
    viewOnline: 'オンラインで見る',
    done: '完了',
    linkCopied: 'リンクをコピーしました',
    emailSent: 'メールを送信しました',
    important: '重要',
    keepRecord: 'この税務書類は会計記録のため大切に保管してください。'
  },
  hi: {
    invoiceGenerated: 'इनवॉइस जारी हुआ',
    success: 'आपका CFDI सफलतापूर्वक जारी किया गया!',
    invoiceNumber: 'इनवॉइस फोलियो',
    uuid: 'UUID',
    company: 'कंपनी',
    generatedAt: 'जारी किया गया',
    downloadOptions: 'डाउनलोड और साझा करें',
    downloadPDF: 'PDF डाउनलोड करें',
    downloadXML: 'XML डाउनलोड करें',
    emailSend: 'ईमेल से भेजें',
    whatsappSend: 'WhatsApp',
    smsSend: 'SMS से भेजें',
    copyLink: 'लिंक कॉपी करें',
    viewOnline: 'ऑनलाइन देखें',
    done: 'पूर्ण',
    linkCopied: 'लिंक कॉपी हो गया',
    emailSent: 'ईमेल भेजा गया',
    important: 'महत्वपूर्ण',
    keepRecord: 'कृपया इस कर रसीद को अपने लेखा रिकॉर्ड के लिए सुरक्षित रखें।'
  },
  de: {
    invoiceGenerated: 'Rechnung erstellt',
    success: 'Ihr CFDI wurde erfolgreich erstellt!',
    invoiceNumber: 'Rechnungsfolio',
    uuid: 'UUID',
    company: 'Unternehmen',
    generatedAt: 'Erstellt am',
    downloadOptions: 'Herunterladen und teilen',
    downloadPDF: 'PDF herunterladen',
    downloadXML: 'XML herunterladen',
    emailSend: 'Per E-Mail senden',
    whatsappSend: 'WhatsApp',
    smsSend: 'Per SMS senden',
    copyLink: 'Link kopieren',
    viewOnline: 'Online ansehen',
    done: 'Fertig',
    linkCopied: 'Link kopiert',
    emailSent: 'E-Mail gesendet',
    important: 'Wichtig',
    keepRecord: 'Bewahren Sie diesen Steuerbeleg für Ihre Buchhaltung auf.'
  },
  it: {
    invoiceGenerated: 'Fattura generata',
    success: 'Il suo CFDI è stato generato con successo!',
    invoiceNumber: 'Folio della fattura',
    uuid: 'UUID',
    company: 'Azienda',
    generatedAt: 'Generato il',
    downloadOptions: 'Scarica e condividi',
    downloadPDF: 'Scarica PDF',
    downloadXML: 'Scarica XML',
    emailSend: 'Invia per e-mail',
    whatsappSend: 'WhatsApp',
    smsSend: 'Invia per SMS',
    copyLink: 'Copia link',
    viewOnline: 'Visualizza online',
    done: 'Fine',
    linkCopied: 'Link copiato',
    emailSent: 'E-mail inviata',
    important: 'Importante',
    keepRecord: 'Conservi questa ricevuta fiscale per la sua contabilità.'
  },
  ru: {
    invoiceGenerated: 'Счёт-фактура сформирована',
    success: 'Ваш CFDI успешно сформирован!',
    invoiceNumber: 'Фолио счёта',
    uuid: 'UUID',
    company: 'Компания',
    generatedAt: 'Сформировано',
    downloadOptions: 'Скачать и поделиться',
    downloadPDF: 'Скачать PDF',
    downloadXML: 'Скачать XML',
    emailSend: 'Отправить по эл. почте',
    whatsappSend: 'WhatsApp',
    smsSend: 'Отправить по SMS',
    copyLink: 'Копировать ссылку',
    viewOnline: 'Посмотреть онлайн',
    done: 'Готово',
    linkCopied: 'Ссылка скопирована',
    emailSent: 'Эл. письмо отправлено',
    important: 'Важно',
    keepRecord: 'Сохраните этот налоговый документ для бухгалтерского учёта.'
  },
  ar: {
    invoiceGenerated: 'تم إنشاء الفاتورة',
    success: 'تم إنشاء CFDI الخاص بكم بنجاح!',
    invoiceNumber: 'رقم الفاتورة',
    uuid: 'UUID',
    company: 'الشركة',
    generatedAt: 'تاريخ الإنشاء',
    downloadOptions: 'التنزيل والمشاركة',
    downloadPDF: 'تنزيل PDF',
    downloadXML: 'تنزيل XML',
    emailSend: 'إرسال بالبريد الإلكتروني',
    whatsappSend: 'WhatsApp',
    smsSend: 'إرسال عبر SMS',
    copyLink: 'نسخ الرابط',
    viewOnline: 'عرض عبر الإنترنت',
    done: 'تم',
    linkCopied: 'تم نسخ الرابط',
    emailSent: 'تم إرسال البريد الإلكتروني',
    important: 'هام',
    keepRecord: 'يرجى الاحتفاظ بهذا الإيصال الضريبي لسجلاتكم المحاسبية.'
  },
  vi: {
    invoiceGenerated: 'Đã tạo hóa đơn',
    success: 'CFDI của quý khách đã được tạo thành công!',
    invoiceNumber: 'Số hóa đơn',
    uuid: 'UUID',
    company: 'Công ty',
    generatedAt: 'Tạo lúc',
    downloadOptions: 'Tải xuống và chia sẻ',
    downloadPDF: 'Tải PDF',
    downloadXML: 'Tải XML',
    emailSend: 'Gửi qua email',
    whatsappSend: 'WhatsApp',
    smsSend: 'Gửi qua SMS',
    copyLink: 'Sao chép liên kết',
    viewOnline: 'Xem trực tuyến',
    done: 'Hoàn tất',
    linkCopied: 'Đã sao chép liên kết',
    emailSent: 'Đã gửi email',
    important: 'Quan trọng',
    keepRecord: 'Vui lòng lưu giữ chứng từ thuế này cho hồ sơ kế toán của quý khách.'
  },
  th: {
    invoiceGenerated: 'สร้างใบกำกับภาษีแล้ว',
    success: 'CFDI ของคุณถูกสร้างเรียบร้อยแล้ว!',
    invoiceNumber: 'เลขที่ใบกำกับภาษี',
    uuid: 'UUID',
    company: 'บริษัท',
    generatedAt: 'สร้างเมื่อ',
    downloadOptions: 'ดาวน์โหลดและแชร์',
    downloadPDF: 'ดาวน์โหลด PDF',
    downloadXML: 'ดาวน์โหลด XML',
    emailSend: 'ส่งทางอีเมล',
    whatsappSend: 'WhatsApp',
    smsSend: 'ส่งทาง SMS',
    copyLink: 'คัดลอกลิงก์',
    viewOnline: 'ดูออนไลน์',
    done: 'เสร็จสิ้น',
    linkCopied: 'คัดลอกลิงก์แล้ว',
    emailSent: 'ส่งอีเมลแล้ว',
    important: 'สำคัญ',
    keepRecord: 'โปรดเก็บเอกสารภาษีนี้ไว้สำหรับบันทึกบัญชีของคุณ'
  },
  nl: {
    invoiceGenerated: 'Factuur gegenereerd',
    success: 'Uw CFDI is met succes gegenereerd!',
    invoiceNumber: 'Factuurfolio',
    uuid: 'UUID',
    company: 'Bedrijf',
    generatedAt: 'Gegenereerd op',
    downloadOptions: 'Downloaden en delen',
    downloadPDF: 'PDF downloaden',
    downloadXML: 'XML downloaden',
    emailSend: 'Per e-mail versturen',
    whatsappSend: 'WhatsApp',
    smsSend: 'Per sms versturen',
    copyLink: 'Link kopiëren',
    viewOnline: 'Online bekijken',
    done: 'Klaar',
    linkCopied: 'Link gekopieerd',
    emailSent: 'E-mail verzonden',
    important: 'Belangrijk',
    keepRecord: 'Bewaar dit belastingbewijs voor uw administratie.'
  }
};
